import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";
import { api } from "../../lib/api";
import "./hpa.css";

const CONSENT_TEXT_VERSION = "v1"; // muss zum Backend-Consent-Text passen

export default function HpaConsent() {
  const [checked, setChecked] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const startAssessment = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/hpa/consent", { text_version: CONSENT_TEXT_VERSION });
      navigate("/hpa/assessment");
    } catch (err) {
      setError(
        err?.response?.data?.detail?.error_code === "HPA_DISABLED"
          ? "Die HPA-Preview ist derzeit nicht verfügbar."
          : "Dein Einverständnis konnte gerade nicht gespeichert werden. Bitte versuche es erneut."
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="hpa hpa-canvas" data-testid="hpa-consent">
      <SEO title="HPA · Einverständnis" description="HPA Consent" path="/hpa/consent" />
      <div className="container-sat max-w-3xl pt-28 pb-24 hpa-thread">
        <div className="pl-6 md:pl-10">
          <div className="hpa-overline mb-8">HPA · Bevor du beginnst</div>

          <h1
            className="hpa-headline text-3xl md:text-4xl mb-8"
            data-testid="hpa-consent-headline"
          >
            Kurz zur <em>Verwendung</em> deiner Antworten.
          </h1>

          <p className="hpa-lede text-base md:text-lg leading-relaxed max-w-xl mb-10">
            Deine Antworten werden ausschließlich verwendet, um deine
            persönliche HPA-Auswertung zu erstellen. Sie werden dir und ggf.
            deiner:m zugewiesenen SAT-Mentor:in zur Reflexion sichtbar gemacht
            und niemals für Werbung, öffentliche Analysen oder Weitergabe an
            Dritte genutzt.
          </p>

          <button
            type="button"
            className="hpa-btn-ghost mb-8"
            onClick={() => setShowDetail((v) => !v)}
            data-testid="hpa-consent-legal-toggle"
            aria-expanded={showDetail}
            aria-controls="hpa-consent-legal-detail"
          >
            {showDetail ? "Details ausblenden" : "Rechtliche Details anzeigen"}
          </button>

          {showDetail && (
            <div
              id="hpa-consent-legal-detail"
              className="hpa-card mb-10 text-[15px] leading-relaxed"
              data-testid="hpa-consent-legal-detail"
            >
              <p className="mb-3">
                <strong className="text-white">Reflexionsmodell.</strong> Die
                HPA ist kein psychologisches Testinstrument und keine
                medizinische Diagnostik. Sie dient der persönlichen Reflexion
                und Orientierung innerhalb der SAT-Akademie.
              </p>
              <p className="mb-3">
                <strong className="text-white">Datenschutz.</strong> Deine
                Antworten werden verschlüsselt gespeichert und nur den zur
                Bearbeitung berechtigten Rollen (dir selbst und ggf.
                zugewiesener SAT-Mentor:in) angezeigt.
              </p>
              <p className="mb-3">
                <strong className="text-white">Widerruf.</strong> Du kannst
                dein Einverständnis jederzeit widerrufen. Deine Daten werden
                dann gemäß SAT-Datenschutzerklärung gelöscht bzw. anonymisiert.
              </p>
              <p>
                <strong className="text-white">Mentor-Review.</strong> Vor
                Freigabe kann dein Profil durch eine:n zugewiesene:n
                SAT-Mentor:in überprüft werden. Diese Person sieht deine
                Auswertung, aber nicht deine einzelnen Rohantworten.
              </p>
            </div>
          )}

          <label
            className="flex items-start gap-4 mb-12 cursor-pointer max-w-xl"
            data-testid="hpa-consent-checkbox-label"
          >
            <input
              type="checkbox"
              className="hpa-checkbox mt-1"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="hpa-consent-checkbox"
            />
            <span className="text-[15px] leading-relaxed text-[color:var(--hpa-text-soft)]">
              Ich stimme der Verarbeitung meiner Antworten für meine
              persönliche HPA-Auswertung zu (Consent-Version{" "}
              <span className="text-[color:var(--hpa-gold)]">
                {CONSENT_TEXT_VERSION}
              </span>
              ).
            </span>
          </label>

          <div className="flex items-center justify-between">
            <Link
              to="/hpa/intro"
              className="hpa-btn-ghost"
              data-testid="hpa-consent-back"
            >
              ← Zurück
            </Link>
            <button
              type="button"
              className="hpa-btn-primary"
              disabled={!checked || submitting}
              onClick={startAssessment}
              data-testid="hpa-consent-start"
            >
              {submitting ? "Wird gestartet…" : "Assessment starten"}
            </button>
          </div>
          {error && (
            <div
              className="mt-6 text-[color:#E8A6A6] text-sm"
              data-testid="hpa-consent-error"
            >
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
